import React, { useCallback, useState } from 'react';
import { StyleSheet } from 'react-native';
import {
  StackSpacer,
  theme,
  useResponsive,
  useToggleState,
} from '@sweetgreen/garnish';

import {
  AccountScreenBorderedSection,
  AccountScreenContainer,
  AccountScreenSection,
  AccountScreenSectionTitle,
  AccountScreenTitle,
  CreditCardAddModal,
  CreditCardEditModal,
  CreditSection,
  PaymentMethodList,
} from '@order/components';
import { useCustomer } from '@order/Customer';
import type { BillingAccount } from '@order/graphql';
import { useCustomerCredit, usePromoCode } from '@order/hooks';
import { useLocalizationContext } from '@order/Localization';

import type { PaymentCreditScreenProps } from '../../navigation/AppNavigation.props';

export const PaymentCreditScreen = (props: PaymentCreditScreenProps) => {
  const { navigation } = props;

  // ─── Context ─────────────────────────────────────────────────────────

  const { customer } = useCustomer();
  const { t } = useLocalizationContext();
  const { currentBreakpoint } = useResponsive();
  const { availableCredit, credits, isLoadingCredits, refetchCredits } =
    useCustomerCredit();

  // ─── Promo Code ──────────────────────────────────────────────────────

  const {
    promoCode,
    promoCodeInvalid,
    isSubmittingPromoCode,
    setPromoCode,
    submitPromoCode,
  } = usePromoCode(refetchCredits);

  // ─── State ───────────────────────────────────────────────────────────

  const [selectedPaymentMethodId, selectPaymentMethodId] = useState<string>();

  // ─── Toggles ─────────────────────────────────────────────────────────

  const {
    value: isShowingAddPaymentMethodModal,
    toggleOn: showAddPaymentMethodModal,
    toggleOff: hideAddPaymentMethodModal,
  } = useToggleState();

  const showEditPaymentMethodModal = useCallback(
    (billingAccount: BillingAccount) => {
      selectPaymentMethodId(billingAccount.id);
    },
    [],
  );

  const hideEditPaymentMethodModal = useCallback(() => {
    selectPaymentMethodId('');
  }, []);

  // ─── Callbacks ───────────────────────────────────────────────────────

  const handlePressAvailableCredit = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditDetails',
    });
  }, [navigation]);

  const handlePressAddPromoCode = useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'CreditAdd',
    });
  }, [navigation]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <AccountScreenContainer screen="payment-credit">
      <AccountScreenTitle
        visibility="COMPACT"
        title={t('account.payment-promo.title')}
      />

      {/* PAYMENT */}
      <AccountScreenSectionTitle title={t('account.payment.title')} />
      <AccountScreenSection
        style={currentBreakpoint.isXS ? styles.mobileGrow : {}}
      >
        <AccountScreenBorderedSection>
          <PaymentMethodList
            items={customer.billingAccounts ?? []}
            onSelectAdd={showAddPaymentMethodModal}
            onSelectItem={showEditPaymentMethodModal}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>

      {/* DIVIDER */}
      {currentBreakpoint.isXS ? (
        <StackSpacer divider={false} customSpacing={theme.spacing['10']} />
      ) : null}

      {/* CREDIT */}
      <AccountScreenSectionTitle title={t('account.credit-promo.title')} />
      <AccountScreenSection>
        <AccountScreenBorderedSection
          style={currentBreakpoint.isXS ? {} : styles.creditSpacing}
        >
          <CreditSection
            creditAmount={availableCredit}
            creditDetails={credits}
            promoCode={promoCode}
            isLoadingCredits={isLoadingCredits}
            isSubmittingPromoCode={isSubmittingPromoCode}
            hasPromoCodeError={promoCodeInvalid}
            onPressAvailableCredit={handlePressAvailableCredit}
            onPressAddPromoCode={handlePressAddPromoCode}
            setPromoCode={setPromoCode}
            onPressApplyPromoCode={submitPromoCode}
          />
        </AccountScreenBorderedSection>
      </AccountScreenSection>

      <CreditCardAddModal
        visible={isShowingAddPaymentMethodModal}
        onRequestClose={hideAddPaymentMethodModal}
      />

      <CreditCardEditModal
        paymentMethodId={selectedPaymentMethodId}
        visible={Boolean(selectedPaymentMethodId)}
        onRequestClose={hideEditPaymentMethodModal}
      />
    </AccountScreenContainer>
  );
};

const styles = StyleSheet.create({
  mobileGrow: {
    flexGrow: 0,
  },
  creditSpacing: {
    paddingVertical: theme.spacing['6'],
  },
});
